import React, { useEffect, useState } from 'react';
import { ShieldCheck, CheckCircle2 } from 'lucide-react';
import { Images } from '@/assets';

interface VerificationSplashScreenProps {
    isReady: boolean;
    hasError?: boolean;
    onFinish: () => void;
}

const statusSteps = [
    'Connecting to Official Fellowship Registry...',
    'Verifying Cryptographic Credential Signature...',
    'Validating Student Record & Project Assets...',
    'Official Credential Authenticated'
];

export const VerificationSplashScreen: React.FC<VerificationSplashScreenProps> = ({
    isReady,
    hasError = false,
    onFinish,
}) => {
    const [progress, setProgress] = useState(18);
    const [stepIndex, setStepIndex] = useState(0);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
        const t1 = setTimeout(() => {
            setProgress(48);
            setStepIndex(1);
        }, 320);

        const t2 = setTimeout(() => {
            setProgress(80);
            setStepIndex(2);
        }, 700);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, []);

    useEffect(() => {
        if (isReady) {
            const finishTimer = setTimeout(() => {
                setProgress(100);
                if (!hasError) {
                    setStepIndex(3);
                    setIsComplete(true);
                }

                const fadeTimer = setTimeout(() => {
                    setIsFadingOut(true);
                    const exitTimer = setTimeout(() => {
                        onFinish();
                    }, 450);
                    return () => clearTimeout(exitTimer);
                }, hasError ? 100 : 500);

                return () => clearTimeout(fadeTimer);
            }, 850);

            return () => clearTimeout(finishTimer);
        }
    }, [isReady, hasError, onFinish]);

    return (
        <div 
            className={`fixed inset-0 z-[100] flex flex-col items-center justify-between px-6 py-10 sm:py-12 bg-[#050A18] text-white transition-opacity duration-500 ease-out select-none ${
                isFadingOut ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}
        >
            {/* Background Ambient High-Tech Glow (contained inside solid background) */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/15 rounded-full blur-[120px]" />
                <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] bg-amber-500/10 rounded-full blur-[90px]" />
            </div>

            {/* Top Brand Pill */}
            <div className="relative z-10 flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-md shadow-sm">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[11px] sm:text-xs font-semibold tracking-wider uppercase text-gray-200">
                    Official Credential Verification
                </span>
            </div>

            {/* Center Visual & Authentication Scanner */}
            <div className="relative z-10 flex flex-col items-center max-w-md w-full text-center space-y-6 sm:space-y-7">
                {/* Logo & Radar Rings */}
                <div className="relative flex items-center justify-center">
                    {/* Pulsing orbital rings */}
                    <div className="absolute w-36 h-36 rounded-full border border-blue-400/20 animate-ping duration-1000" />
                    <div className="absolute w-32 h-32 rounded-full border border-amber-400/25 animate-pulse" />
                    
                    {/* Crisp White Logo Container */}
                    <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-white shadow-2xl shadow-blue-500/20 flex items-center justify-center p-3.5 sm:p-4 transition-transform duration-300">
                        <img 
                            src={Images.tpaRealLogo} 
                            alt="The Purpose Academy" 
                            className="w-full h-full object-contain"
                        />
                    </div>
                </div>

                {/* Brand & Fellowship Title */}
                <div className="space-y-1">
                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
                        THE PURPOSE ACADEMY
                    </h2>
                    <p className="text-xs sm:text-sm font-bold text-[#E07B12] tracking-widest uppercase">
                        AI Impact Fellowship
                    </p>
                </div>

                {/* Progress Bar */}
                <div className="w-full max-w-xs space-y-2">
                    <div className="relative w-full h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-gradient-to-r from-blue-500 via-indigo-400 to-[#E07B12] transition-all duration-300 ease-out rounded-full shadow-[0_0_12px_rgba(224,123,18,0.7)]"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <div className="flex justify-between items-center text-[10.5px] font-mono text-gray-400 px-0.5 tracking-wider">
                        <span>VERIFYING CREDENTIAL</span>
                        <span className="text-white font-bold">{progress}%</span>
                    </div>
                </div>

                {/* Dynamic Status Ticker */}
                <div className="min-h-12 flex items-center justify-center px-4">
                    {isComplete ? (
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs sm:text-sm font-semibold animate-in zoom-in-95 duration-200 shadow-lg shadow-emerald-950/50">
                            <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
                            <span>Official Credential Authenticated</span>
                        </div>
                    ) : hasError ? (
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/20 border border-red-400/40 text-red-300 text-xs sm:text-sm font-semibold">
                            <span>Completing Lookup...</span>
                        </div>
                    ) : (
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs sm:text-sm font-medium text-gray-200 backdrop-blur-xs">
                            <ShieldCheck size={16} className="text-blue-400 animate-pulse shrink-0" />
                            <span className="animate-in fade-in duration-200">
                                {statusSteps[stepIndex]}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Security / Trust Footer */}
            <div className="relative z-10 text-center space-y-1 text-gray-400 text-[11px] sm:text-xs">
                <div className="flex items-center justify-center gap-2 text-gray-300 font-medium">
                    <ShieldCheck size={15} className="text-emerald-400" />
                    <span>256-Bit Cryptographically Sealed Credential</span>
                </div>
                <p className="text-gray-400 text-[10px] tracking-wide">
                    Official Digital Ledger &bull; The Purpose Academy
                </p>
            </div>
        </div>
    );
};

export default VerificationSplashScreen;
